import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import {
  Search,
  X,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Upload,
  Download,
  FileText,
  CheckCircle2,
  HelpCircle,
  AlertTriangle,
  Eye,
  Users,
  XCircle,
  ArrowLeftRight as ObTransactionsLogoIcon
} from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import TableSortLabel from '@mui/material/TableSortLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

const StatWidget = ({ title, value, icon: Icon, color, onClick, active }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)'
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const parseOrdinante = (description) => {
  if (!description) return '-';
  const match = String(description).match(/Ordinante:\s*([^;]+)/i);
  return match && match[1] ? match[1].trim() : '-';
};

const normalizeOrdinanteSearchText = (ordinanteRaw) => {
  const normalized = String(ordinanteRaw || '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';

  const cutTokens = [
    ' VIA ',
    ' VIALE ',
    ' PIAZZA ',
    ' CORSO ',
    ' LARGO ',
    ' VICOLO ',
    ' CONTRADA ',
    ' LOCALITA ',
    ' CAP ',
    ' PROVINCIA ',
    ' DATA ',
    ' ORDINE ',
    ' ULTERIORI ',
    ' DESCRIZIONI ',
    ' RIF '
  ];

  let candidate = ` ${normalized} `;
  for (const token of cutTokens) {
    const idx = candidate.indexOf(token);
    if (idx > -1) {
      candidate = candidate.slice(0, idx + 1);
    }
  }

  const stopWords = new Set(['BONIFICO', 'ISTANTANEO', 'A', 'CREDITO', 'ADDEBITO', 'SEPA', 'RIF', 'N', 'NR', 'NUMERO', 'CIVICO']);
  const tokens = candidate
    .trim()
    .split(' ')
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !/^\d+$/.test(t) && !stopWords.has(t));

  return tokens.join(' ').trim();
};

export default function OpenBankingTransactionsCsv() {
  const { addJob } = useJob();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchSeqRef = useRef(0);

  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const startOfMonthIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

  const formatDisplayDate = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  };

  const extractFilenameFromDisposition = (disposition) => {
    const raw = String(disposition || '');
    const match =
      raw.match(/filename\*=UTF-8''([^;]+)$/i) ||
      raw.match(/filename\*=UTF-8''([^;]+);/i) ||
      raw.match(/filename="?([^"]+)"?/i);
    if (!match || !match[1]) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (_) {
      return match[1];
    }
  };

  const [filters, setFilters] = useState({
    account_name: '',
    from_date: startOfMonthIso,
    to_date: todayIso,
    stato_movimento: 'all'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [orderBy, setOrderBy] = useState('data_contabile');
  const [order, setOrder] = useState('desc');
  const [searchDescription, setSearchDescription] = useState('');
  const [amountFilter, setAmountFilter] = useState('all');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageRow, setManageRow] = useState(null);
  const [manageIgnore, setManageIgnore] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [ordinanteDialogOpen, setOrdinanteDialogOpen] = useState(false);
  const [ordinanteDialogRow, setOrdinanteDialogRow] = useState(null);
  const [ordinanteTab, setOrdinanteTab] = useState(0);
  const [detailClientMatches, setDetailClientMatches] = useState([]);
  const [detailClientMatchesLoading, setDetailClientMatchesLoading] = useState(false);
  const [detailClientMatchesLoaded, setDetailClientMatchesLoaded] = useState(false);
  const [detailClientMatchesError, setDetailClientMatchesError] = useState('');
  const [detailOrdinante, setDetailOrdinante] = useState('');
  const [detailCrmMatches, setDetailCrmMatches] = useState([]);
  const [detailCrmMatchesLoading, setDetailCrmMatchesLoading] = useState(false);
  const [detailCrmMatchesLoaded, setDetailCrmMatchesLoaded] = useState(false);
  const [detailCrmMatchesError, setDetailCrmMatchesError] = useState('');
  const [ordinanteMatchTotalsByTxId, setOrdinanteMatchTotalsByTxId] = useState({});
  const [ordinanteMatchFilter, setOrdinanteMatchFilter] = useState('all');
  const [totalItems, setTotalItems] = useState(0);
  const [summaryCredit, setSummaryCredit] = useState(0);
  const [summaryDebit, setSummaryDebit] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [viewTab, setViewTab] = useState(0);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importAccountUuid, setImportAccountUuid] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [downloadingLastImport, setDownloadingLastImport] = useState(false);
  const [latestImportAvailable, setLatestImportAvailable] = useState(false);
  const [latestImportChecking, setLatestImportChecking] = useState(false);
  const [latestImportLog, setLatestImportLog] = useState(null);
  const [importLogs, setImportLogs] = useState([]);
  const [importLogsTotal, setImportLogsTotal] = useState(0);
  const [importLogsPage, setImportLogsPage] = useState(0);
  const [importLogsRowsPerPage, setImportLogsRowsPerPage] = useState(10);
  const [importLogsLoading, setImportLogsLoading] = useState(false);
  const [importLogsError, setImportLogsError] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('gestito');

  const importLogsFetchSeqRef = useRef(0);
  const latestImportFetchSeqRef = useRef(0);

  const normalizeAccountName = (value) =>
    String(value || '').trim().toUpperCase();

  const accountByName = useMemo(() => {
    const map = {};
    accounts.forEach((acc) => {
      const key = normalizeAccountName(acc.name);
      if (key && !map[key]) {
        map[key] = acc;
      }
    });
    return map;
  }, [accounts]);

  const formatAccountSelectLabel = (acc) => {
    const company = acc.business_name || 'Azienda N/D';
    const provider = acc.provider_name || 'Provider N/D';
    const accountName = acc.name || '-';
    return `${company} · ${provider} · ${accountName}`;
  };

  const getAccountLabelByName = (accountName) => {
    const key = normalizeAccountName(accountName);
    if (!key) return 'Tutti gli IBAN';
    const acc = accountByName[key];
    if (!acc) return accountName;
    return formatAccountSelectLabel(acc);
  };

  const getAccountInfoByName = (accountName) => {
    const key = normalizeAccountName(accountName);
    if (!key) return null;
    return accountByName[key] || null;
  };

  const formatSize = (bytes) => {
    const num = Number(bytes || 0);
    if (!num) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(num) / Math.log(1024));
    const value = num / Math.pow(1024, i);
    return `${value.toFixed(1)} ${units[i]}`;
  };

  const buildFilterParams = (sourceFilters) => {
    const params = {};
    const accountName = (sourceFilters.account_name || '').trim();
    const from = (sourceFilters.from_date || '').trim();
    const to = (sourceFilters.to_date || '').trim();
    const statoMovimento = (sourceFilters.stato_movimento || '').trim();

    if (accountName) params.account_name = accountName;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    if (statoMovimento && statoMovimento !== 'all') {
      params.stato_movimento = statoMovimento;
    }
    if (searchDescription.trim()) params.search = searchDescription.trim();
    if (amountFilter && amountFilter !== 'all') {
      params.amount_filter = amountFilter;
    }

    return params;
  };

  const fetchData = async (options = {}) => {
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setLoading(true);
    try {
      const { nextPage, nextRowsPerPage, nextOrderBy, nextOrder, nextFilters } =
        options;
      const { nextOrdinanteMatchFilter } = options;

      const effectiveFilters = nextFilters || filters;
      const params = buildFilterParams(effectiveFilters);

      const pageToUse =
        typeof nextPage === 'number' && nextPage >= 0 ? nextPage : page;
      const limitToUse =
        typeof nextRowsPerPage === 'number' && nextRowsPerPage > 0
          ? nextRowsPerPage
          : rowsPerPage;
      const orderByToUse = nextOrderBy || orderBy;
      const orderToUse = nextOrder || order;

      params.page = pageToUse + 1;
      params.limit = limitToUse;
      params.orderBy = orderByToUse;
      params.orderDirection = orderToUse;
      params.includeMatchSummary = 1;
      const ordinanteMatchToUse =
        nextOrdinanteMatchFilter !== undefined
          ? nextOrdinanteMatchFilter
          : ordinanteMatchFilter;
      if (ordinanteMatchToUse && ordinanteMatchToUse !== 'all') {
        params.ordinanteMatchFilter = ordinanteMatchToUse;
      }

      const res = await api.get('/open-banking/transactions-csv', { params });
      const data = res.data || {};
      if (fetchSeqRef.current !== seq) return;

      if (Array.isArray(data)) {
        setRows(data);
        setTotalItems(data.length);
        setSummaryCredit(0);
        setSummaryDebit(0);
        setOrdinanteMatchTotalsByTxId((prev) => {
          const next = { ...prev };
          (data || []).forEach((row) => {
            if (!row || row.id == null) return;
            const total = row.ordinante_match_total;
            if (total === undefined) return;
            next[String(row.id)] = Number.isFinite(Number(total))
              ? Math.max(0, Math.min(2, Number(total)))
              : null;
          });
          return next;
        });
      } else {
        const list = Array.isArray(data.data) ? data.data : [];
        setRows(list);
        setTotalItems(data.total || 0);
        setSummaryCredit(data.totalCredit || 0);
        setSummaryDebit(data.totalDebit || 0);
        setOrdinanteMatchTotalsByTxId((prev) => {
          const next = { ...prev };
          list.forEach((row) => {
            if (!row || row.id == null) return;
            const total = row.ordinante_match_total;
            if (total === undefined) return;
            next[String(row.id)] = Number.isFinite(Number(total))
              ? Math.max(0, Math.min(2, Number(total)))
              : null;
          });
          return next;
        });
      }
    } catch (error) {
      console.error('Error fetching CSV transactions', error);
      if (fetchSeqRef.current === seq) {
        toast.error('Errore nel caricamento delle transazioni CSV');
      }
    } finally {
      if (fetchSeqRef.current === seq) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData({ nextFilters: filters, nextPage: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await api.get('/open-banking/accounts');
        setAccounts(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Error fetching accounts for CSV transactions', error);
      } finally {
        setAccountsLoaded(true);
      }
    };
    loadAccounts();
  }, []);

  useEffect(() => {
    setPage(0);
    fetchData({ nextPage: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountFilter]);

  const applyFilters = (nextFilters) => {
    setPage(0);
    setFilters(nextFilters);
    fetchData({ nextFilters, nextPage: 0 });
  };

  const clearField = (field) => {
    const next = { ...filters, [field]: '' };
    setFilters(next);
    applyFilters(next);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchData({ nextPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    fetchData({ nextPage: 0, nextRowsPerPage: value });
  };

  const handleImportLogsChangePage = (_event, newPage) => {
    setImportLogsPage(newPage);
  };

  const handleImportLogsChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setImportLogsRowsPerPage(value);
    setImportLogsPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    setOrder(nextOrder);
    setOrderBy(property);
    setPage(0);
    fetchData({
      nextPage: 0,
      nextOrderBy: property,
      nextOrder
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    try {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    } catch {
      return amount;
    }
  };

  const amountColor = (amount) => {
    if (amount == null) return undefined;
    if (amount > 0) return 'success.main';
    if (amount < 0) return 'error.main';
    return 'text.primary';
  };

  const getStatoTooltip = (row) => {
    const raw = row.stato_movimento;
    if (!raw || raw === 'da_gestire') {
      return 'Da gestire';
    }
    const base =
      raw === 'gestito'
        ? 'Gestito'
        : raw === 'non_gestire'
        ? 'Non gestire'
        : 'Stato sconosciuto';

    const when = row.stato_movimento_updated_at
      ? new Date(row.stato_movimento_updated_at).toLocaleString('it-IT')
      : null;
    const who =
      row.stato_movimento_updated_by_name ||
      (row.stato_movimento_updated_by != null
        ? `Utente #${row.stato_movimento_updated_by}`
        : null);

    if (who && when) return `${base} da ${who} il ${when}`;
    if (who) return `${base} da ${who}`;
    if (when) return `${base} il ${when}`;
    return base;
  };

  const openManageDialog = (row) => {
    setManageRow(row);
    setManageIgnore(false);
    setManageDialogOpen(true);
  };

  const openDetailDialog = (row) => {
    setDetailRow(row);
    setDetailDialogOpen(true);
  };

  const openOrdinanteDialog = (row) => {
    setOrdinanteDialogRow(row);
    setOrdinanteTab(0);
    setDetailClientMatches([]);
    setDetailClientMatchesLoading(false);
    setDetailClientMatchesLoaded(false);
    setDetailClientMatchesError('');
    setDetailOrdinante('');
    setDetailCrmMatches([]);
    setDetailCrmMatchesLoading(false);
    setDetailCrmMatchesLoaded(false);
    setDetailCrmMatchesError('');
    setOrdinanteDialogOpen(true);
  };

  useEffect(() => {
    const loadClientMatches = async () => {
      if (
        !ordinanteDialogOpen ||
        ordinanteTab !== 0 ||
        !ordinanteDialogRow?.id ||
        detailClientMatchesLoaded
      ) {
        return;
      }
      try {
        setDetailClientMatchesLoading(true);
        setDetailClientMatchesError('');
        const res = await api.get(
          `/open-banking/transactions-csv/${ordinanteDialogRow.id}/clienti-match`
        );
        const payload = res.data || {};
        setDetailClientMatches(Array.isArray(payload.data) ? payload.data : []);
        setDetailOrdinante(payload.ordinante || '');
        setOrdinanteMatchTotalsByTxId((prev) => {
          const id = ordinanteDialogRow.id;
          const next = { ...prev };
          const total = Number(payload.total || 0);
          next[id] = Number.isFinite(total) ? Math.max(0, Math.min(2, total)) : null;
          return next;
        });
        setDetailClientMatchesLoaded(true);
      } catch (error) {
        console.error('Error loading clienti match for CSV transaction', error);
        setDetailClientMatchesError(
          error?.response?.data?.message || 'Errore nel caricamento dei clienti associati'
        );
      } finally {
        setDetailClientMatchesLoading(false);
      }
    };
    loadClientMatches();
  }, [ordinanteDialogOpen, ordinanteTab, ordinanteDialogRow, detailClientMatchesLoaded]);

  useEffect(() => {
    const loadCrmMatches = async () => {
      if (!ordinanteDialogOpen || ordinanteTab !== 1 || detailCrmMatchesLoaded) {
        return;
      }

      const ordinanteText = String(detailOrdinante || parseOrdinante(ordinanteDialogRow?.descrizione) || '').trim();
      const search = normalizeOrdinanteSearchText(ordinanteText) || ordinanteText;
      if (!search || String(search).trim().length < 3) {
        setDetailCrmMatches([]);
        setDetailCrmMatchesError('');
        setDetailCrmMatchesLoaded(true);
        return;
      }

      try {
        setDetailCrmMatchesLoading(true);
        setDetailCrmMatchesError('');
        const res = await api.get('/customer/lookup/chat', {
          params: { search, limit: 20 }
        });
        const items = Array.isArray(res.data) ? res.data : [];
        setDetailCrmMatches(items);
        setDetailCrmMatchesLoaded(true);
      } catch (error) {
        setDetailCrmMatchesError(error?.response?.data?.message || 'Errore nel caricamento dei clienti CRM');
      } finally {
        setDetailCrmMatchesLoading(false);
      }
    };

    loadCrmMatches();
  }, [detailCrmMatchesLoaded, detailOrdinante, ordinanteDialogOpen, ordinanteDialogRow, ordinanteTab]);

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    if (importing) return;
    setImportDialogOpen(false);
    setImportFile(null);
  };

  const handleImportCsv = async () => {
    if (!importAccountUuid) {
      toast.error('Seleziona un account');
      return;
    }
    if (!importFile) {
      toast.error('Seleziona un file CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('account_uuid', importAccountUuid);
      formData.append('file', importFile);

      const res = await api.post('/open-banking/transactions-csv/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error('Job ID non ricevuto dal server');
      }
      addJob(jobId, 'open_banking_tx_csv_import');
      toast.info(
        res.data?.message ||
          'Importazione transazioni CSV avviata. Riceverai notifica in campanella.'
      );

      const importedAccount = accounts.find((acc) => acc.uuid === importAccountUuid);
      const importedAccountName = importedAccount?.name || '';
      const nextFilters = importedAccountName
        ? { ...filters, account_name: importedAccountName }
        : filters;
      setFilters(nextFilters);

      setImportDialogOpen(false);
      setImportFile(null);
      setPage(0);
    } catch (error) {
      console.error('Error importing CSV transactions', error);
      toast.error(error?.response?.data?.message || error?.message || 'Errore importazione CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadLatestImportCsv = async () => {
    if (!importAccountUuid) {
      toast.error('Seleziona un account');
      return;
    }
    if (downloadingLastImport) return;

    try {
      setDownloadingLastImport(true);
      const ownerId = Number(latestImportLog?.imported_by_user_id || 0);
      const currentUserId = Number(user?.id || 0);
      if (ownerId && currentUserId && ownerId !== currentUserId) {
        const ownerLabel =
          String(latestImportLog?.imported_by_user_name || '').trim() ||
          `utente #${ownerId}`;
        const whenLabel = latestImportLog?.imported_at
          ? new Date(latestImportLog.imported_at).toLocaleDateString('it-IT')
          : '';
        toast.info(`File caricato da ${ownerLabel}${whenLabel ? ` il ${whenLabel}` : ''}`);
      }
      const res = await api.get('/open-banking/transactions-csv/import-log/latest/download', {
        params: { account_uuid: importAccountUuid },
        responseType: 'blob'
      });

      const mime = res.headers?.['content-type'] || 'text/csv';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);

      const filename =
        extractFilenameFromDisposition(res.headers?.['content-disposition']) ||
        `transazioni_import_${importAccountUuid}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading latest CSV import:', error);
      toast.error(error?.response?.data?.message || 'Errore durante il download del CSV importato');
    } finally {
      setDownloadingLastImport(false);
    }
  };

  useEffect(() => {
    if (!importDialogOpen || !importAccountUuid) {
      setLatestImportAvailable(false);
      setLatestImportChecking(false);
      return;
    }

    const seq = latestImportFetchSeqRef.current + 1;
    latestImportFetchSeqRef.current = seq;
    setLatestImportChecking(true);

    const load = async () => {
      try {
        const res = await api.get('/open-banking/transactions-csv/import-log', {
          params: {
            account_uuid: importAccountUuid,
            page: 1,
            limit: 1
          }
        });
        if (latestImportFetchSeqRef.current !== seq) return;
        const first = Array.isArray(res?.data?.data) ? res.data.data[0] : null;
        const available = Number(first?.file_size || 0) > 0;
        setLatestImportAvailable(available);
        setLatestImportLog(first || null);
      } catch (_) {
        if (latestImportFetchSeqRef.current !== seq) return;
        setLatestImportAvailable(false);
        setLatestImportLog(null);
      } finally {
        if (latestImportFetchSeqRef.current === seq) {
          setLatestImportChecking(false);
        }
      }
    };

    load();
  }, [importDialogOpen, importAccountUuid]);

  const fetchImportLogs = async (options = {}) => {
    const seq = importLogsFetchSeqRef.current + 1;
    importLogsFetchSeqRef.current = seq;
    setImportLogsLoading(true);
    setImportLogsError('');

    try {
      const selectedName =
        options.accountName !== undefined ? options.accountName : filters.account_name;
      const acc = selectedName ? getAccountInfoByName(selectedName) : null;
      const accountUuid = acc?.uuid || '';

      const pageToLoad =
        options.page !== undefined ? options.page : importLogsPage;
      const limitToLoad =
        options.limit !== undefined ? options.limit : importLogsRowsPerPage;

      const params = {
        page: Number(pageToLoad || 0) + 1,
        limit: limitToLoad
      };
      if (accountUuid) params.account_uuid = accountUuid;

      const res = await api.get('/open-banking/transactions-csv/import-log', { params });
      if (importLogsFetchSeqRef.current !== seq) return;
      const payload = res?.data || {};
      setImportLogs(Array.isArray(payload.data) ? payload.data : []);
      setImportLogsTotal(Number(payload.total || 0));
    } catch (error) {
      if (importLogsFetchSeqRef.current !== seq) return;
      console.error('Error fetching CSV import logs:', error);
      setImportLogsError(
        error?.response?.data?.message || 'Errore nel caricamento dello storico import CSV'
      );
      setImportLogs([]);
      setImportLogsTotal(0);
    } finally {
      if (importLogsFetchSeqRef.current === seq) {
        setImportLogsLoading(false);
      }
    }
  };

  const handleDownloadImportLogById = async (row) => {
    const id = row?.id;
    if (!id) return;

    try {
      const ownerId = Number(row?.imported_by_user_id || 0);
      const currentUserId = Number(user?.id || 0);
      if (ownerId && currentUserId && ownerId !== currentUserId) {
        const ownerLabel =
          String(row?.imported_by_user_name || '').trim() || `utente #${ownerId}`;
        const whenLabel = row?.imported_at
          ? new Date(row.imported_at).toLocaleDateString('it-IT')
          : '';
        toast.info(`File caricato da ${ownerLabel}${whenLabel ? ` il ${whenLabel}` : ''}`);
      }
      const res = await api.get(`/open-banking/transactions-csv/import-log/${id}/download`, {
        responseType: 'blob'
      });

      const mime = res.headers?.['content-type'] || 'text/csv';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);

      const fallbackName = row?.file_name || `transazioni_import_${id}.csv`;
      const filename =
        extractFilenameFromDisposition(res.headers?.['content-disposition']) || fallbackName;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV import by id:', error);
      toast.error(error?.response?.data?.message || 'Errore durante il download del CSV importato');
    }
  };

  const handleStartMovimentiReport = async (format = 'pdf') => {
    if (reportLoading) return;
    const fromDate = (filters.from_date || '').trim();
    const toDate = (filters.to_date || '').trim();

    if (!fromDate || !toDate) {
      toast.error('Imposta un intervallo date per generare il report');
      return;
    }

    try {
      setReportLoading(true);
      const payload = {
        accountNames: filters.account_name ? [filters.account_name] : [],
        fromDate,
        toDate,
        amountFilter: amountFilter || 'all',
        stato_movimento: filters.stato_movimento || 'all',
        search: searchDescription.trim(),
        format // Passiamo il formato richiesto: 'pdf' o 'csv'
      };
      const res = await api.post('/open-banking/transactions-csv/statement', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) {
        throw new Error('jobId non ricevuto');
      }
      addJob(jobId, 'open_banking_statement');
      setReportDialogOpen(false);
      toast.success('Report transazioni da CSV avviato: lo troverai nelle notifiche');
    } catch (error) {
      console.error('Errore avvio report transazioni da CSV:', error);
      toast.error("Errore durante l'avvio del report transazioni da CSV");
    } finally {
      setReportLoading(false);
    }
  };

  const handleConfirmManage = async () => {
    if (!manageRow) {
      setManageDialogOpen(false);
      return;
    }
    const newState = manageIgnore ? 'non_gestire' : 'gestito';
    try {
      const res = await api.patch(
        `/open-banking/transactions-csv/${manageRow.id}/stato-movimento`,
        { stato_movimento: newState }
      );
      toast.success(res.data?.message || 'Movimento aggiornato');
      setManageDialogOpen(false);
      setManageRow(null);
      setRows((prev) =>
        prev.map((r) => (r.id === manageRow.id ? { ...r, stato_movimento: newState } : r))
      );
    } catch (error) {
      console.error('Error updating CSV stato_movimento', error);
      toast.error("Errore durante l'aggiornamento del movimento");
    }
  };

  const pageRows = useMemo(() => rows, [rows]);
  const visibleRows = useMemo(() => pageRows, [pageRows]);
  const totalTransactions = Number(totalItems || 0);
  const totalCredit = Number(summaryCredit || 0);
  const totalDebit = Number(summaryDebit || 0);
  const pageRowIds = useMemo(
    () => pageRows.map((row) => row.id).filter((id) => id != null),
    [pageRows]
  );
  const visibleRowIds = pageRowIds;
  const allVisibleSelected =
    visibleRowIds.length > 0 && visibleRowIds.every((id) => selectedRows.includes(id));

  useEffect(() => {
    setImportLogsPage(0);
  }, [filters.account_name]);

  useEffect(() => {
    if (!accountsLoaded) return;
    if (viewTab !== 1) return;
    fetchImportLogs();
  }, [filters.account_name, importLogsPage, importLogsRowsPerPage, accountsLoaded, viewTab]);

  useEffect(() => {
    const missing = pageRows
      .filter((row) => row && row.id != null)
      .map((row) => row.id)
      .filter((id) => {
        const rowTotal = pageRows.find((r) => r && r.id === id)?.ordinante_match_total;
        if (rowTotal !== undefined) return false;
        return ordinanteMatchTotalsByTxId[String(id)] === undefined;
      });
    if (missing.length === 0) return undefined;

    let active = true;
    const load = async () => {
      const results = await Promise.all(
        missing.map(async (id) => {
          try {
            const res = await api.get(`/open-banking/transactions-csv/${id}/clienti-match`);
            const payload = res.data || {};
            const total = Number(payload.total || 0);
            return { id, total: Number.isFinite(total) ? Math.max(0, Math.min(2, total)) : null };
          } catch (_) {
            return { id, total: null };
          }
        })
      );
      if (!active) return;
      setOrdinanteMatchTotalsByTxId((prev) => {
        const next = { ...prev };
        results.forEach((r) => {
          next[String(r.id)] = r.total;
        });
        return next;
      });
    };
    load();

    return () => {
      active = false;
    };
  }, [pageRowIds, pageRows, ordinanteMatchTotalsByTxId]);

  useEffect(() => {
    setSelectedRows((prev) => prev.filter((id) => visibleRowIds.includes(id)));
  }, [visibleRowIds]);

  const handleToggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRows((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
      return;
    }
    setSelectedRows((prev) => {
      const merged = new Set([...prev, ...visibleRowIds]);
      return Array.from(merged);
    });
  };

  const handleSelectAllFilteredTransactions = async () => {
    if (totalItems > 0 && selectedRows.length >= totalItems) {
      setSelectedRows([]);
      return;
    }

    try {
      const params = buildFilterParams(filters);
      params.page = 1;
      params.limit = totalItems || 10000;
      params.orderBy = orderBy;
      params.orderDirection = order;

      const res = await api.get('/open-banking/transactions-csv', { params });
      const data = res.data || {};
      const allRows = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      const allIds = allRows.map((item) => item.id).filter((id) => id != null);

      setSelectedRows(allIds);
      toast.success(`Selezionati ${allIds.length} movimenti (in base ai filtri correnti)`);
    } catch (error) {
      console.error('Error selecting all filtered CSV transactions', error);
      toast.error('Errore nel selezionare tutti i movimenti filtrati');
    }
  };

  const handleBulkUpdateStatoMovimento = async (nextStatoMovimento) => {
    if (!Array.isArray(selectedRows) || selectedRows.length === 0) {
      toast.error('Seleziona almeno un movimento');
      return false;
    }

    try {
      const res = await api.patch('/open-banking/transactions-csv/bulk/stato-movimento', {
        ids: selectedRows,
        stato_movimento: nextStatoMovimento
      });

      toast.success(
        res?.data?.message ||
          `Aggiornati ${selectedRows.length} movimenti`
      );
      setSelectedRows([]);
      fetchData({ nextPage: page });
      return true;
    } catch (error) {
      console.error('Error bulk update CSV stato_movimento', error);
      toast.error(
        error?.response?.data?.message ||
          "Errore durante l'aggiornamento massivo dei movimenti"
      );
      return false;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ObTransactionsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Transazioni (da csv)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Movimenti bancari importati da file CSV
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Upload size={18} />}
            onClick={handleOpenImportDialog}
          >
            Importa
          </Button>
          <Button
            variant="contained"
            startIcon={<FileText size={18} />}
            onClick={handleOpenReportDialog}
          >
            Report Transazioni
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <StatWidget
            title="Totale transazioni"
            value={totalTransactions.toLocaleString('it-IT')}
            icon={CreditCard}
            color="#0ea5e9"
            onClick={() => setAmountFilter('all')}
            active={amountFilter === 'all'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatWidget
            title="Accrediti"
            value={formatCurrency(totalCredit)}
            icon={TrendingUp}
            color="#16a34a"
            onClick={() => setAmountFilter('credit')}
            active={amountFilter === 'credit'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatWidget
            title="Addebiti"
            value={formatCurrency(totalDebit)}
            icon={TrendingDown}
            color="#dc2626"
            onClick={() => setAmountFilter('debit')}
            active={amountFilter === 'debit'}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Tabs
          value={viewTab}
          onChange={(_event, value) => setViewTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Movimenti" />
          <Tab label="Storico import CSV" />
        </Tabs>
      </Box>

      {viewTab === 0 && (
      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', overflowX: 'hidden' }}>
            <Box sx={{ width: 200, flexShrink: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>IBAN</InputLabel>
                <Select
                  label="IBAN"
                  value={filters.account_name}
                  onChange={(event) => {
                    const value = event.target.value;
                    const next = { ...filters, account_name: value };
                    setFilters(next);
                    applyFilters(next);
                  }}
                  disabled={!accountsLoaded}
                >
                  <MenuItem value="">
                    <em>Tutti gli IBAN</em>
                  </MenuItem>
                  {accounts.filter((acc) => acc.name).map((acc) => (
                    <MenuItem key={acc.uuid} value={acc.name || ''}>
                      {formatAccountSelectLabel(acc)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Descrizione / Note"
                value={searchDescription}
                onChange={(e) => setSearchDescription(e.target.value)}
                onBlur={() => applyFilters(filters)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#637381" />
                    </InputAdornment>
                  ),
                  endAdornment: searchDescription ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchDescription('');
                          applyFilters(filters);
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>
            <Box sx={{ width: 140, flexShrink: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato</InputLabel>
                <Select
                  label="Stato"
                  value={filters.stato_movimento}
                  onChange={(event) => {
                    const value = event.target.value;
                    const next = { ...filters, stato_movimento: value };
                    setFilters(next);
                    applyFilters(next);
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="da_gestire">Da gestire</MenuItem>
                  <MenuItem value="gestito">Gestito</MenuItem>
                  <MenuItem value="non_gestire">Non gestire</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: 180, flexShrink: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Match ordinante</InputLabel>
                <Select
                  label="Match ordinante"
                  value={ordinanteMatchFilter}
                  onChange={(event) => {
                    const value = event.target.value;
                    setOrdinanteMatchFilter(value);
                    setPage(0);
                    fetchData({ nextPage: 0, nextOrdinanteMatchFilter: value });
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="none">Nessun match</MenuItem>
                  <MenuItem value="single">Match unico</MenuItem>
                  <MenuItem value="multi">Più match</MenuItem>
                  <MenuItem value="unknown">Non disponibile</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: 135, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                label="Data da"
                type="date"
                value={filters.from_date}
                onChange={(event) => {
                  const value = event.target.value;
                  const next = { ...filters, from_date: value };
                  setFilters(next);
                  applyFilters(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ width: 135, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                label="Data a"
                type="date"
                value={filters.to_date}
                onChange={(event) => {
                  const value = event.target.value;
                  const next = { ...filters, to_date: value };
                  setFilters(next);
                  applyFilters(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Filtri attivi
            </Typography>
            {filters.account_name && (
              <Chip
                size="small"
                label={`IBAN: ${getAccountLabelByName(filters.account_name)}`}
                onDelete={() => clearField('account_name')}
              />
            )}
            {amountFilter === 'credit' && (
              <Chip
                size="small"
                label="Solo accrediti"
                onDelete={() => setAmountFilter('all')}
              />
            )}
            {amountFilter === 'debit' && (
              <Chip
                size="small"
                label="Solo addebiti"
                onDelete={() => setAmountFilter('all')}
              />
            )}
            {filters.stato_movimento && filters.stato_movimento !== 'all' && (
              <Chip
                size="small"
                label={
                  filters.stato_movimento === 'da_gestire'
                    ? 'Da gestire'
                    : filters.stato_movimento === 'gestito'
                    ? 'Gestito'
                    : 'Non gestire'
                }
                onDelete={() => {
                  const next = { ...filters, stato_movimento: 'all' };
                  setFilters(next);
                  applyFilters(next);
                }}
              />
            )}
            {ordinanteMatchFilter !== 'all' && (
              <Chip
                size="small"
                label={
                  ordinanteMatchFilter === 'none'
                    ? 'Ordinante: nessun match'
                    : ordinanteMatchFilter === 'single'
                    ? 'Ordinante: match unico'
                    : ordinanteMatchFilter === 'multi'
                    ? 'Ordinante: più match'
                    : 'Ordinante: non disponibile'
                }
                onDelete={() => {
                  setOrdinanteMatchFilter('all');
                  setPage(0);
                  fetchData({ nextPage: 0, nextOrdinanteMatchFilter: 'all' });
                }}
              />
            )}
            {filters.from_date && (
              <Chip
                size="small"
                label={`Da: ${formatDisplayDate(filters.from_date)}`}
                onDelete={() => clearField('from_date')}
              />
            )}
            {filters.to_date && (
              <Chip
                size="small"
                label={`A: ${formatDisplayDate(filters.to_date)}`}
                onDelete={() => clearField('to_date')}
              />
            )}
            {!filters.account_name &&
              !filters.from_date &&
              !filters.to_date &&
              amountFilter === 'all' &&
              ordinanteMatchFilter === 'all' && (
                <Typography variant="caption" color="text.secondary">
                  Nessun filtro applicato
                </Typography>
              )}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedRows.length > 0
                ? `Selezionati ${selectedRows.length} movimenti`
                : 'Nessun movimento selezionato'}
              {totalItems > 0 && ` (totale ${totalItems} con i filtri correnti)`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedRows.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<DoneAllIcon fontSize="small" />}
                  onClick={() => {
                    setBulkStatusValue('gestito');
                    setBulkStatusDialogOpen(true);
                  }}
                >
                  Cambia stato
                </Button>
              )}
              {totalItems > 0 && (
                <Tooltip
                  title={
                    selectedRows.length >= totalItems
                      ? 'Deseleziona tutti i movimenti filtrati'
                      : 'Seleziona tutti i movimenti filtrati'
                  }
                >
                  <IconButton
                    color="primary"
                    onClick={handleSelectAllFilteredTransactions}
                    sx={{
                      bgcolor: 'primary.50',
                      border: '1px solid',
                      borderColor: 'primary.200',
                      '&:hover': { bgcolor: 'primary.100' }
                    }}
                  >
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={allVisibleSelected}
                    indeterminate={selectedRows.length > 0 && !allVisibleSelected}
                    onChange={handleToggleSelectAllVisible}
                  />
                </TableCell>
                <TableCell align="center">Da gestire</TableCell>
                <TableCell sortDirection={orderBy === 'iban' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'iban'}
                    direction={orderBy === 'iban' ? order : 'asc'}
                    onClick={() => handleRequestSort('iban')}
                  >
                    IBAN
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'data_contabile' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'data_contabile'}
                    direction={orderBy === 'data_contabile' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_contabile')}
                  >
                    Data contabile
                  </TableSortLabel>
                </TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Ordinante</TableCell>
                <TableCell sortDirection={orderBy === 'causale_abi' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'causale_abi'}
                    direction={orderBy === 'causale_abi' ? order : 'asc'}
                    onClick={() => handleRequestSort('causale_abi')}
                  >
                    Causale
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'importo' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'importo'}
                    direction={orderBy === 'importo' ? order : 'asc'}
                    onClick={() => handleRequestSort('importo')}
                  >
                    Importo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'type' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Dettaglio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessuna transazione trovata.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleToggleRowSelection(row.id)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 32
                        }}
                      >
                        {row.stato_movimento === 'gestito' ? (
                          <Tooltip title={getStatoTooltip(row)} arrow>
                            <CheckCircle2 size={20} color="#22c55e" />
                          </Tooltip>
                        ) : row.stato_movimento === 'non_gestire' ? (
                          <Tooltip title={getStatoTooltip(row)} arrow>
                            <XCircle size={20} color="#dc2626" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Gestisci movimento" arrow>
                            <IconButton size="small" onClick={() => openManageDialog(row)}>
                              <HelpCircle size={20} color="#dc2626" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.iban || '-'}
                      </Typography>
                      {(() => {
                        const acc = getAccountInfoByName(row.iban);
                        if (!acc) return null;
                        return (
                          <Typography variant="caption" color="text.secondary">
                            {(acc.business_name || 'Azienda N/D') + ' · ' + (acc.provider_name || 'Provider N/D')}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {row.data_contabile
                        ? new Date(row.data_contabile).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.descrizione || '-'}</Typography>
                      {row.note && (
                        <Typography variant="caption" color="text.secondary">
                          {row.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2">{parseOrdinante(row.descrizione)}</Typography>
                        {(() => {
                          const raw =
                            row?.ordinante_match_total !== undefined
                              ? row.ordinante_match_total
                              : ordinanteMatchTotalsByTxId[String(row.id)];
                          const total = Number.isFinite(Number(raw)) ? Number(raw) : null;

                          let title = 'Stato match non disponibile (apri Analisi ordinante)';
                          if (total === 0) {
                            title =
                              'Nessun match: dall’ordinante non è stato riconosciuto alcun nominativo (apri Analisi ordinante)';
                          } else if (total === 1) {
                            title = 'Match unico: trovato un solo nominativo (apri Analisi ordinante)';
                          } else if (total != null && total >= 2) {
                            title = 'Più match: trovati più nominativi possibili (apri Analisi ordinante)';
                          }

                          return (
                            <Tooltip title={title} arrow>
                              <IconButton
                                size="small"
                                onClick={() => openOrdinanteDialog(row)}
                                sx={(theme) => {
                                  let color = '#94a3b8';
                                  if (total === 0) color = theme.palette.error.main;
                                  else if (total === 1) color = theme.palette.success.main;
                                  else if (total != null && total >= 2) color = theme.palette.warning.main;
                                  return { p: 0.5, color };
                                }}
                              >
                                <Users size={16} />
                              </IconButton>
                            </Tooltip>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.causale_abi || '-'}
                        {row.causale_interna ? ` / ${row.causale_interna}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: amountColor(row.importo) }}
                      >
                        {formatCurrency(row.importo)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Number(row.importo || 0) >= 0 ? 'Accredito' : 'Addebito'}
                        size="small"
                        color={Number(row.importo || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openDetailDialog(row)}
                        title="Dettaglio transazione"
                      >
                        <Eye size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[30, 60, 90]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
      )}

      {viewTab === 1 && (
      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FileText size={18} color="#0f172a" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Storico import CSV
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filters.account_name
                    ? `Account: ${getAccountLabelByName(filters.account_name)}`
                    : 'Tutti gli account'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 320 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>IBAN</InputLabel>
                  <Select
                    label="IBAN"
                    value={filters.account_name}
                    onChange={(event) => {
                      const value = event.target.value;
                      setImportLogsPage(0);
                      const next = { ...filters, account_name: value };
                      applyFilters(next);
                    }}
                    disabled={!accountsLoaded}
                  >
                    <MenuItem value="">
                      <em>Tutti gli IBAN</em>
                    </MenuItem>
                    {accounts.filter((acc) => acc.name).map((acc) => (
                      <MenuItem key={acc.uuid} value={acc.name || ''}>
                        {formatAccountSelectLabel(acc)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Chip
                size="small"
                label={`${importLogsTotal.toLocaleString('it-IT')} import`}
                sx={{ bgcolor: '#f1f5f9', color: '#0f172a', fontWeight: 700, borderRadius: '999px' }}
              />
            </Box>
          </Box>

          {importLogsError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importLogsError}
            </Alert>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Importato il</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Righe</TableCell>
                <TableCell align="right">Inserite</TableCell>
                <TableCell align="right">Scartate</TableCell>
                <TableCell align="right">Dimensione</TableCell>
                <TableCell align="right">Scarica</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {importLogsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : importLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessun import trovato.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                importLogs.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.imported_at
                          ? new Date(row.imported_at).toLocaleString('it-IT')
                          : '-'}
                      </Typography>
                      {row.imported_by_user_name ? (
                        <Typography variant="caption" color="text.secondary">
                          Da {row.imported_by_user_name}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.account_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {row.file_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.total_rows || 0).toLocaleString('it-IT')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {Number(row.inserted_rows || 0).toLocaleString('it-IT')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: Number(row.skipped_rows || 0) > 0 ? 'warning.main' : 'text.secondary' }}
                      >
                        {Number(row.skipped_rows || 0).toLocaleString('it-IT')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {formatSize(row.file_size)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const hasFile = Number(row.file_size || 0) > 0;
                        return (
                          <Tooltip title={hasFile ? 'Scarica CSV' : 'File non disponibile'} arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadImportLogById(row)}
                                disabled={!hasFile}
                              >
                                <Download size={18} color={hasFile ? '#16a34a' : '#dc2626'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={importLogsTotal}
          rowsPerPage={importLogsRowsPerPage}
          page={importLogsPage}
          onPageChange={handleImportLogsChangePage}
          onRowsPerPageChange={handleImportLogsChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
      )}

      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cambia stato movimenti selezionati</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scegli il nuovo stato da applicare a {selectedRows.length} movimenti selezionati.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-status-label">Nuovo stato</InputLabel>
            <Select
              labelId="bulk-status-label"
              label="Nuovo stato"
              value={bulkStatusValue}
              onChange={(event) => setBulkStatusValue(event.target.value)}
            >
              <MenuItem value="da_gestire">Da gestire</MenuItem>
              <MenuItem value="gestito">Segna come gestiti</MenuItem>
              <MenuItem value="non_gestire">Non gestire</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const ok = await handleBulkUpdateStatoMovimento(bulkStatusValue);
              if (ok) setBulkStatusDialogOpen(false);
            }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportDialogOpen}
        onClose={() => !reportLoading && setReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conferma generazione Report Transazioni da CSV</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Scegli il formato del report. Verrà generato un report asincrono con le transazioni da CSV filtrate e sarà disponibile nelle notifiche.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {filters.account_name ? (
              <Chip size="small" label={`IBAN: ${getAccountLabelByName(filters.account_name)}`} />
            ) : (
              <Chip size="small" label="Tutti gli IBAN" />
            )}
            <Chip
              size="small"
              label={`Periodo: ${formatDisplayDate(filters.from_date)} - ${formatDisplayDate(filters.to_date)}`}
            />
            <Chip
              size="small"
              label={`Importi: ${
                amountFilter === 'credit'
                  ? 'Solo accrediti'
                  : amountFilter === 'debit'
                  ? 'Solo addebiti'
                  : 'Accrediti e addebiti'
              }`}
            />
            {filters.stato_movimento && filters.stato_movimento !== 'all' && (
              <Chip
                size="small"
                label={
                  filters.stato_movimento === 'da_gestire'
                    ? 'Stato: Da gestire'
                    : filters.stato_movimento === 'gestito'
                    ? 'Stato: Gestito'
                    : 'Stato: Non gestire'
                }
              />
            )}
            {searchDescription.trim() && (
              <Chip size="small" label={`Ricerca: ${searchDescription.trim()}`} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Annulla
          </Button>
          <Button onClick={() => handleStartMovimentiReport('csv')} variant="outlined" disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera CSV'}
          </Button>
          <Button onClick={() => handleStartMovimentiReport('pdf')} variant="contained" disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Importa transazioni da CSV</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                Seleziona account e file CSV. Le righe verranno importate su
                <strong> transazioni_csv </strong>
                e associate all&apos;account scelto.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Account</InputLabel>
                <Select
                  label="Account"
                  value={importAccountUuid}
                  onChange={(event) => setImportAccountUuid(event.target.value)}
                  disabled={!accountsLoaded || importing}
                >
                  {accounts.filter((acc) => acc.name).map((acc) => (
                    <MenuItem key={acc.uuid} value={acc.uuid}>
                      {formatAccountSelectLabel(acc)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="file"
                inputProps={{ accept: '.csv,text/csv' }}
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                disabled={importing}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importing}>
            Annulla
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download size={14} color={latestImportAvailable ? '#16a34a' : '#dc2626'} />}
            onClick={handleDownloadLatestImportCsv}
            disabled={
              importing ||
              downloadingLastImport ||
              latestImportChecking ||
              !importAccountUuid ||
              !latestImportAvailable
            }
          >
            {downloadingLastImport ? 'Scaricamento...' : 'Scarica ultimo CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Upload size={14} />}
            onClick={handleImportCsv}
            disabled={importing}
          >
            {importing ? 'Importazione...' : 'Importa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AlertTriangle size={20} color="#f59e0b" />
            </Box>
            <Typography variant="h6">Gestisci movimento</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {manageRow && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}
              >
                Transazione CSV
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                  rowGap: 0.75,
                  columnGap: 3
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  IBAN
                </Typography>
                <Typography variant="body2">
                  {manageRow.iban || '-'}
                  {(() => {
                    const acc = getAccountInfoByName(manageRow.iban);
                    if (!acc) return null;
                    return ` (${acc.business_name || 'Azienda N/D'} · ${acc.provider_name || 'Provider N/D'})`;
                  })()}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body2">
                  {Number(manageRow.importo || 0) >= 0 ? 'Accredito' : 'Addebito'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Importo
                </Typography>
                <Typography variant="body2">{formatCurrency(manageRow.importo)}</Typography>

                <Typography variant="body2" color="text.secondary">
                  Data contabile
                </Typography>
                <Typography variant="body2">
                  {manageRow.data_contabile
                    ? new Date(manageRow.data_contabile).toLocaleDateString('it-IT')
                    : '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Ordinante
                </Typography>
                <Typography variant="body2">
                  {parseOrdinante(manageRow.descrizione)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Descrizione
                </Typography>
                <Typography variant="body2">
                  {manageRow.descrizione || '-'}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Vuoi contrassegnare questo movimento come gestito?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Questa azione serve per tracciare internamente se il movimento è stato preso in carico
              o non richiede ulteriori attività.
            </Typography>
          </Box>
          <RadioGroup
            row
            value={manageIgnore ? 'non_gestire' : 'gestito'}
            onChange={(e) => setManageIgnore(e.target.value === 'non_gestire')}
            sx={{ mt: 1, columnGap: 4 }}
          >
            <FormControlLabel
              value="gestito"
              control={<Radio color="primary" />}
              label="Sì, il movimento è stato gestito"
            />
            <FormControlLabel
              value="non_gestire"
              control={<Radio color="warning" />}
              label="Il movimento non è da gestire"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleConfirmManage}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dettaglio transazione CSV</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Informazioni generali
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                    rowGap: 0.75,
                    columnGap: 3,
                    mt: 1
                  }}
                >
                  <Typography color="text.secondary">ID</Typography>
                  <Typography>{detailRow.id || '-'}</Typography>
                  <Typography color="text.secondary">IBAN</Typography>
                  <Typography>
                    {detailRow.iban || '-'}
                    {(() => {
                      const acc = getAccountInfoByName(detailRow.iban);
                      if (!acc) return null;
                      return ` (${acc.business_name || 'Azienda N/D'} · ${acc.provider_name || 'Provider N/D'})`;
                    })()}
                  </Typography>
                  <Typography color="text.secondary">Data contabile</Typography>
                  <Typography>
                    {detailRow.data_contabile
                      ? new Date(detailRow.data_contabile).toLocaleDateString('it-IT')
                      : '-'}
                  </Typography>
                  <Typography color="text.secondary">Data valuta</Typography>
                  <Typography>
                    {detailRow.data_valuta
                      ? new Date(detailRow.data_valuta).toLocaleDateString('it-IT')
                      : '-'}
                  </Typography>
                  <Typography color="text.secondary">Importo</Typography>
                  <Typography>{formatCurrency(detailRow.importo)}</Typography>
                  <Typography color="text.secondary">Tipo</Typography>
                  <Typography>
                    {Number(detailRow.importo || 0) >= 0 ? 'Accredito' : 'Addebito'}
                  </Typography>
                  <Typography color="text.secondary">Stato movimento</Typography>
                  <Typography>{detailRow.stato_movimento || '-'}</Typography>
                  <Typography color="text.secondary">Causale ABI</Typography>
                  <Typography>{detailRow.causale_abi || '-'}</Typography>
                  <Typography color="text.secondary">Causale interna</Typography>
                  <Typography>{detailRow.causale_interna || '-'}</Typography>
                  <Typography color="text.secondary">Nome file</Typography>
                  <Typography>{detailRow.nome_file || '-'}</Typography>
                  <Typography color="text.secondary">Riga CSV</Typography>
                  <Typography>{detailRow.riga_csv ?? '-'}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Descrizione
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">{detailRow.descrizione || '-'}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Note
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">{detailRow.note || '-'}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ordinanteDialogOpen}
        onClose={() => setOrdinanteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Analisi ordinante</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={ordinanteTab}
            onChange={(_event, next) => setOrdinanteTab(next)}
            sx={{ mb: 2 }}
          >
            <Tab label="Clienti Passpartout" />
            <Tab label="Clienti CRM" />
          </Tabs>
          {ordinanteTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Match cliente da ordinante
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {detailOrdinante || parseOrdinante(ordinanteDialogRow?.descrizione) || '-'}
                </Typography>
              </Box>

              {detailClientMatchesLoading ? (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : detailClientMatchesError ? (
                <Alert severity="error">{detailClientMatchesError}</Alert>
              ) : detailClientMatches.length === 0 ? (
                <Alert severity="info">Nessun cliente Passpartout trovato con il match su ordinante.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Località</TableCell>
                        <TableCell>Telefono</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>P.IVA / CF</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailClientMatches.map((item) => {
                        const localita = [item.localita, item.cap, item.provincia]
                          .filter(Boolean)
                          .join(' - ');
                        return (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {item.display_name || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Codice: {item.codice || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{localita || '-'}</TableCell>
                            <TableCell>{item.telefono || '-'}</TableCell>
                            <TableCell>{item.email || '-'}</TableCell>
                            <TableCell>
                              {item.partita_iva || item.codice_fiscale
                                ? [item.partita_iva, item.codice_fiscale]
                                    .filter(Boolean)
                                    .join(' / ')
                                : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          {ordinanteTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Ricerca clienti CRM da ordinante
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {detailOrdinante || parseOrdinante(ordinanteDialogRow?.descrizione) || '-'}
                </Typography>
              </Box>

              {detailCrmMatchesLoading ? (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : detailCrmMatchesError ? (
                <Alert severity="error">{detailCrmMatchesError}</Alert>
              ) : detailCrmMatches.length === 0 ? (
                <Alert severity="info">Nessun cliente CRM trovato.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Indirizzo</TableCell>
                        <TableCell>Telefono</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailCrmMatches.map((item) => {
                        const addressLine1 = String(item.strada_residenza || '').trim();
                        const addressLine2 = [
                          String(item.cap_residenza || '').trim(),
                          String(item.citta_residenza || '').trim(),
                          String(item.prov_residenza || '').trim()
                        ]
                          .filter(Boolean)
                          .join(' ');
                        const address = [addressLine1, addressLine2].filter(Boolean).join(' · ');

                        return (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {item.full_name || [item.surname, item.name].filter(Boolean).join(' ') || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {item.id ?? '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{address || '-'}</TableCell>
                            <TableCell>{item.telephone || '-'}</TableCell>
                            <TableCell>{item.email || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrdinanteDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
